import { authAdapter } from '@/app/_auth/auth'
import { useAuth } from '@/app/_auth/auth-hooks'
import { useDomainFromUrl } from '@/app/_common/domain/use-domain-from-url'
import { RelativePath } from '@/hooks/api/_paths'
import React from 'react'

type ApiRequestProps = {
  type?: 'DEFAULT' | 'MULTIPART'
  options?: RequestInit
  path: RelativePath
  queryParams?: Record<string, string>
}

/**
 * Makes a request to the specified API with the given path and options.
 *
 * @param {string} options.path - The relative URL path to query.
 * @param {ApiType} options.api - The API to target, 'users' or 'task-manager'.
 * @param {RequestInit} [options.options] - Additional options to pass to the fetch call.
 * @returns {Promise<Response>} The response from the API.
 */
async function handleRequest(
  url: URL,
  type: 'DEFAULT' | 'MULTIPART',
  options: RequestInit = {},
): Promise<Response> {
  try {
    const { token, clientId } = await getRequestData()

    const headers = {
      Accept: '*/*',
      Authorization: `Bearer ${token}`,
      'X-Client-Id': clientId,
    }

    if (type === 'DEFAULT') {
      ;(headers as any)['Content-Type'] = 'application/json'
    }

    const requestOptions: RequestInit = {
      credentials: 'include',
      mode: 'cors',
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    }

    const response = await fetch(url, requestOptions)

    return response
  } catch (error) {
    console.error(error)
    throw error
  }
}

async function getRequestData(): Promise<{ token: string | undefined; clientId: string }> {
  if (import.meta.env.VITE_CLIENT_ID) {
    return { token: undefined, clientId: import.meta.env.VITE_CLIENT_ID }
  }

  if (!authAdapter) {
    throw new Error('AUTH_ADAPTER_NOT_INITIALIZED')
  }

  const token = await authAdapter.getToken()
  const clientId = authAdapter.getUserId()

  if (!clientId) {
    throw new Error('FAILED_TO_GET_CLIENT_ID')
  }

  return { token, clientId }
}

function useApiRequestWithoutDomain(): {
  userId: string | null
  apiRequest: (props: ApiRequestProps) => Promise<Response>
} {
  const { apiBaseUrl, user } = useAuth()
  const [userId, setUserId] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (authAdapter) {
      setUserId(authAdapter.getUserId() ?? user?.uid ?? null)
    } else {
      setUserId(user?.uid ?? null)
    }
  }, [user, apiBaseUrl])

  return {
    userId,
    apiRequest: ({ path, type = 'DEFAULT', options }) => {
      if (!apiBaseUrl) throw new Error('API_BASE_URL_NOT_FOUND')

      const requestUrl = new URL(path, apiBaseUrl)

      return handleRequest(requestUrl, type, options)
    },
  }
}

function useApiRequest(): {
  apiRequest: (props: ApiRequestProps) => Promise<Response>
  domainId: string
  userId: string | null
} {
  const { apiBaseUrl, user } = useAuth()
  const { domainId } = useDomainFromUrl()
  const [userId, setUserId] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (authAdapter) {
      setUserId(authAdapter.getUserId() ?? user?.uid ?? null)
    } else {
      setUserId(user?.uid ?? null)
    }
  }, [user, apiBaseUrl])

  return {
    domainId,
    userId,
    apiRequest: ({ path, queryParams, type = 'DEFAULT', options }: ApiRequestProps) => {
      if (!apiBaseUrl) throw new Error('API_BASE_URL_NOT_FOUND')

      const requestUrl = new URL(path, apiBaseUrl)

      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          requestUrl.searchParams.set(key, value)
        })
      }

      return handleRequest(requestUrl, type, options)
    },
  }
}

export { handleRequest, useApiRequest, useApiRequestWithoutDomain }
